import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SidePanelShellProps {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Whether to close the panel when the Escape key is pressed. Default true. */
  closeOnEscape?: boolean;
  /** Extra classes for the outer container. */
  className?: string;
}

/**
 * Generic side-panel shell. Renders a flex-column with a sticky header
 * (title + close button) and a scrollable body.
 *
 * This is the abstracted shell used originally by the TT Orders V2
 * `OrderSidePanel`. Substantive content is provided by the caller via
 * children so each feature can render whatever it needs.
 */
export function SidePanelShell({
  title,
  onClose,
  children,
  closeOnEscape = true,
  className,
}: SidePanelShellProps) {
  useEffect(() => {
    if (!closeOnEscape) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, closeOnEscape]);

  return (
    <div className={`flex flex-col h-full ${className ?? ""}`}>
      <div className="flex items-center justify-between p-3 border-b">
        <h3 className="text-sm font-semibold">{title}</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-3">{children}</div>
    </div>
  );
}
